import {
  applyDiceStage,
  applyRollStage,
  applyRulesStage,
  rollAttack,
  rollDefense,
  type CombatContext,
} from '../combat/pipeline';
import { DEFAULT_MODIFIERS } from '../combat/modifiers';
import { lairAdjacent, neighborsOf } from '../selectors';
import type { CombatSnapshot, LootKind, PlayerId, RegionId, TerritoryId } from '../types';
import type { Ctx } from './context';
import { afterRoll, snapshotOf, snapshotOutcome } from './combat';
import { addEffect } from './effects';
import { checkLootLimit } from './loot';
import { gainHarmony } from './actions';
import { drawTerritoryCard } from './cards';
import { startTurn } from './turn';

/** Fase de boss no início de cada rodada. */
export function bossPhase(ctx: Ctx): void {
  const { s, rng } = ctx;
  if (s.boss) {
    s.boss.timer--;
    ctx.events.push({ type: 'bossTimer', boss: s.boss.id, timer: s.boss.timer });
    if (s.boss.timer <= 0) raid(ctx);
  }
  if (!s.boss) {
    const roll = rng.die();
    const spawn = s.info.ruleset.bossSpawnFaces.includes(roll);
    ctx.events.push({ type: 'bossRoll', roll, spawn });
    if (spawn) rollRegion(ctx);
  }
}

/** Tira até `amount` tropas, deixando pelo menos 1. */
function hurt(ctx: Ctx, t: TerritoryId, amount: number, losses: Map<TerritoryId, number>): void {
  const st = ctx.s.territories[t];
  const lost = Math.max(0, Math.min(amount, st.troops - 1));
  st.troops -= lost;
  losses.set(t, (losses.get(t) ?? 0) + lost);
}

export function raid(ctx: Ctx): void {
  const { s, rng } = ctx;
  const boss = s.boss!;
  const adjacent = lairAdjacent(s, boss.region);
  const losses = new Map<TerritoryId, number>();
  const frozen: TerritoryId[] = [];
  const discards: { player: PlayerId; count: number }[] = [];
  const r = boss.raid;
  switch (r.type) {
    case 'damageAdjacent':
      for (const t of adjacent) hurt(ctx, t, r.amount, losses);
      break;
    case 'quake': {
      for (const t of adjacent) hurt(ctx, t, r.adjacent, losses);
      const ring = new Set<TerritoryId>();
      for (const t of adjacent) for (const n of neighborsOf(s, t)) if (!adjacent.includes(n)) ring.add(n);
      for (const t of ring) hurt(ctx, t, r.ring2, losses);
      break;
    }
    case 'halveStrongest': {
      const max = Math.max(...adjacent.map((t) => s.territories[t].troops));
      for (const t of adjacent) if (s.territories[t].troops === max) hurt(ctx, t, Math.floor(max / 2), losses);
      break;
    }
    case 'freeze':
      for (const t of adjacent) {
        hurt(ctx, t, r.amount, losses);
        addEffect(ctx, 'frozen', null, { at: 'roundStart', round: s.round + r.duration }, { territory: t });
        frozen.push(t);
      }
      break;
    case 'damageAdjacentAndDiscard':
      for (const t of adjacent) hurt(ctx, t, r.amount, losses);
      for (const pl of s.players) {
        if (!pl.alive) continue;
        let count = 0;
        for (let i = 0; i < r.discard && pl.cards.length; i++) {
          const [card] = pl.cards.splice(rng.int(pl.cards.length), 1);
          s.discard.push(card);
          count++;
        }
        discards.push({ player: pl.id, count });
      }
      break;
  }
  ctx.events.push({
    type: 'bossRaid',
    boss: boss.id,
    region: boss.region,
    raid: r.type,
    losses: [...losses].map(([territory, lost]) => ({ territory, lost })),
    frozen,
    discards,
  });
  s.bossDiscard.push(boss.id);
  s.boss = null;
}

function rollRegion(ctx: Ctx): void {
  const { s, rng } = ctx;
  const die = s.info.content.map.regionDie;
  const chooser = s.eliminationOrder[0] ?? null;
  const rolls = chooser === null ? [rng.die(die.length)] : [rng.die(die.length), rng.die(die.length)];
  const regions = rolls.map((r) => die[r - 1]);
  ctx.events.push({ type: 'bossRegionRoll', rolls, regions, chooser });
  if (chooser !== null && regions[0] !== regions[1]) {
    s.pending = { kind: 'bossRegion', player: chooser, regions };
    return;
  }
  spawnBoss(ctx, regions[0]);
}

export function spawnBoss(ctx: Ctx, region: RegionId): void {
  const { s, rng } = ctx;
  if (s.bossDeck.length === 0) {
    s.bossDeck = rng.shuffle(s.bossDiscard);
    s.bossDiscard = [];
  }
  const id = s.bossDeck.shift();
  if (!id) return;
  const def = s.info.content.bosses.find((b) => b.id === id)!;
  s.boss = {
    id,
    region,
    hp: def.hp,
    maxHp: def.hp,
    timer: def.timer,
    defenseDice: def.defenseDice,
    lootDraw: def.lootDraw,
    raid: def.raid,
    damage: {},
  };
  ctx.events.push({ type: 'bossSpawn', boss: id, region });
}

/** Resolução da escolha do mestre dos bosses; em seguida começa o turno. */
export function chooseBossRegion(ctx: Ctx, region: RegionId): void {
  ctx.s.pending = null;
  spawnBoss(ctx, region);
  startTurn(ctx, ctx.s.current);
}

export function bossCombatContext(ctx: Ctx, p: PlayerId, from: TerritoryId): CombatContext {
  const { s } = ctx;
  return {
    state: s,
    kind: 'boss',
    attacker: p,
    defender: null,
    from,
    to: null,
    route: 'lair',
    sea: false,
    attackDice: Math.min(s.info.ruleset.maxAttackDice, s.territories[from].troops - 1),
    defenseDice: s.boss!.defenseDice,
    attackRolls: [],
    defenseRolls: [],
    tieGoesTo: 'defender',
    defenderWinsAll: false,
    notes: [],
  };
}

export function attackBoss(ctx: Ctx, p: PlayerId, from: TerritoryId): void {
  const c = bossCombatContext(ctx, p, from);
  applyDiceStage(c, DEFAULT_MODIFIERS);
  applyRulesStage(c, DEFAULT_MODIFIERS);
  rollAttack(c, ctx.rng);
  rollDefense(c, ctx.rng);
  applyRollStage(c, ctx.rng, 'initial', DEFAULT_MODIFIERS);
  afterRoll(ctx, p, snapshotOf(c, false));
}

export function resolveBossCombat(ctx: Ctx, p: PlayerId, snap: CombatSnapshot): void {
  const { s } = ctx;
  const boss = s.boss!;
  const out = snapshotOutcome(ctx, p, snap);
  const from = s.territories[snap.from];
  from.troops -= out.attackerLoss;
  s.turn.lost += out.attackerLoss;
  gainHarmony(ctx, p);
  boss.hp = Math.max(0, boss.hp - out.defenderLoss);
  ctx.events.push({
    type: 'bossCombat',
    player: p,
    from: snap.from,
    boss: boss.id,
    attackRolls: snap.attackRolls,
    defenseRolls: snap.defenseRolls,
    attackerLoss: out.attackerLoss,
    bossLoss: out.defenderLoss,
    hpLeft: boss.hp,
    notes: snap.notes,
  });
  // Recompensa por dano: 1 carta de território a cada N de vida tirada deste boss.
  if (out.defenderLoss > 0) {
    const per = s.info.ruleset.bossDamagePerCard;
    const before = boss.damage[p] ?? 0;
    const after = before + out.defenderLoss;
    boss.damage[p] = after;
    const cards = per > 0 ? Math.floor(after / per) - Math.floor(before / per) : 0;
    if (cards > 0) {
      for (let i = 0; i < cards; i++) drawTerritoryCard(ctx, p);
      ctx.events.push({ type: 'bossDamageReward', boss: boss.id, player: p, damage: after, cards });
    }
  }
  if (boss.hp === 0) killBoss(ctx, p);
}

function killBoss(ctx: Ctx, p: PlayerId): void {
  const { s, rng } = ctx;
  const boss = s.boss!;
  s.players[p].bossKills++;
  s.bossDiscard.push(boss.id);
  s.boss = null;
  ctx.events.push({ type: 'bossKilled', boss: boss.id, player: p });
  const options: LootKind[] = [];
  for (let i = 0; i < boss.lootDraw; i++) {
    if (s.lootDeck.length === 0 && s.lootDiscard.length > 0) {
      s.lootDeck = rng.shuffle(s.lootDiscard);
      s.lootDiscard = [];
    }
    const card = s.lootDeck.shift();
    if (card) options.push(card);
  }
  ctx.events.push({ type: 'lootDrawn', player: p, options });
  if (options.length === 1) {
    s.players[p].loot.push(options[0]);
    ctx.events.push({ type: 'lootChosen', player: p, kind: options[0] });
    checkLootLimit(ctx, p);
  } else if (options.length > 1) {
    s.pending = { kind: 'lootChoice', player: p, options };
  }
}
